import * as React from 'react';
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ModalOverlayProps as AriaModalOverlayProps,
  ModalRenderProps as AriaModalRenderProps,
} from 'react-aria-components';
import { OverlayTriggerStateContext } from 'react-aria-components';
import { createPortal } from 'react-dom';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { cx } from '@/utils/cx';

export const SLIDEOUT_PANEL_BASE =
  'fixed top-0 right-0 bottom-0 h-full w-full shadow-xl pointer-events-auto';
export const SLIDEOUT_ANIM_ENTER =
  'data-[entering]:animate-in data-[entering]:slide-in-from-right data-[entering]:duration-300 data-[entering]:fade-in-0';
export const SLIDEOUT_ANIM_EXIT =
  'data-[exiting]:animate-out data-[exiting]:slide-out-to-right data-[exiting]:duration-500 data-[exiting]:fade-out-0';
export const SLIDEOUT_DIALOG_INNER =
  'relative flex size-full flex-col items-start overflow-clip bg-primary ring-1 ring-secondary_alt outline-hidden';

/**
 * Bottom-anchored variants — used by the modal path's `Modal` component
 * (slideout-menu.tsx) when `placement='bottom'` (e.g. the report drill
 * panel, which comes up from the bottom with the chart staying visible
 * behind it). `NonModalSlideout` below does not consume these; it remains
 * right-anchored only.
 */
// `bg-primary` on the OUTER panel (not just the inner dialog's
// SLIDEOUT_DIALOG_INNER) is load-bearing: the `pb-[env(safe-area-inset-bottom)]`
// padding lifts the opaque inner dialog up on devices with a home indicator, so
// without a surface background here the exposed safe-area strip is transparent
// and the page/chart shows through behind the sheet. Painting the panel itself
// keeps that inset covered.
export const SLIDEOUT_PANEL_BASE_BOTTOM =
  'fixed bottom-0 right-0 left-0 w-full pb-[env(safe-area-inset-bottom)] bg-primary shadow-xl pointer-events-auto';
export const SLIDEOUT_ANIM_ENTER_BOTTOM =
  'data-[entering]:animate-in data-[entering]:slide-in-from-bottom data-[entering]:duration-300 data-[entering]:fade-in-0';
export const SLIDEOUT_ANIM_EXIT_BOTTOM =
  'data-[exiting]:animate-out data-[exiting]:slide-out-to-bottom data-[exiting]:duration-500 data-[exiting]:fade-out-0';

export type SlideoutPlacement = 'right' | 'bottom';

/**
 * Shared style computation for the slideout panel's z-index and enter/exit
 * translate distance. Used identically by the modal path's `Modal`
 * component (slideout-menu.tsx) and `NonModalSlideout` below — keeping it
 * in one place means a change to the panel's stacking or animation offset
 * can't silently diverge between the two render paths.
 *
 * `placement` defaults to `'right'` (every existing caller's behavior is
 * unchanged). When `'bottom'`, `size` is interpreted as a height instead of
 * a width, and the enter/exit transform is set on the Y axis.
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared style helper co-located with the component that owns it
export function computeSlideoutPanelStyle(
  zIndex: number,
  size: number | undefined,
  placement: SlideoutPlacement = 'right'
): React.CSSProperties {
  if (placement === 'bottom') {
    return {
      zIndex,
      '--tw-enter-translate-y': '100%',
      '--tw-exit-translate-y': '100%',
      ...(size
        ? {
            // Cap against the app's visual-viewport height, not 100vh: on
            // mobile browsers 100vh includes the retractable URL/toolbar
            // chrome, so a bottom panel sized to it extends below the visible
            // viewport. `--app-viewport-height` tracks window.visualViewport
            // (set by useViewportHeight); 100dvh is the fallback when it is
            // unset. Mirrors the ModalOverlay underlay's min-height.
            height: `min(${size}px, var(--app-viewport-height, 100dvh))`,
            maxHeight: `min(${size}px, var(--app-viewport-height, 100dvh))`,
          }
        : {}),
    } as React.CSSProperties;
  }
  return {
    zIndex,
    '--tw-enter-translate-x': '100%',
    '--tw-exit-translate-x': '100%',
    ...(size
      ? {
          width: `min(${size}px, 100vw)`,
          maxWidth: `min(${size}px, 100vw)`,
        }
      : {}),
  } as React.CSSProperties;
}

type SlideoutPhase = 'entering' | 'open' | 'exiting' | 'closed';

function useSlideoutPhase(isOpen: boolean): SlideoutPhase {
  const [phase, setPhase] = useState<SlideoutPhase>(
    isOpen ? 'entering' : 'closed'
  );
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const enterTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    if (
      isOpen &&
      (phaseRef.current === 'closed' || phaseRef.current === 'exiting')
    ) {
      setPhase('entering');
    } else if (
      !isOpen &&
      (phaseRef.current === 'open' || phaseRef.current === 'entering')
    ) {
      setPhase('exiting');
    }
  }, [isOpen]);

  useEffect(() => {
    if (phase === 'entering') {
      clearTimeout(enterTimer.current);
      enterTimer.current = setTimeout(() => setPhase('open'), 300);
    } else if (phase === 'exiting') {
      clearTimeout(enterTimer.current);
      enterTimer.current = setTimeout(() => setPhase('closed'), 500);
    }
    return () => {
      clearTimeout(enterTimer.current);
    };
  }, [phase]);

  return phase;
}

// --- Stack-aware Escape coordination for non-modal slideouts ---
//
// A modal slideout gets Escape scoping for free from react-aria's overlay
// machinery. A non-modal slideout has none of that, so it listens for Escape
// on `document` directly. With a naive per-instance listener, mounting more
// than one non-modal slideout means a single Escape fires every instance's
// handler and closes them all at once — losing the stacking UX where Escape
// should dismiss only the topmost panel.
//
// We keep a module-level stack of the mounted, keyboard-dismissable instances
// (in mount order, which matches visual stacking order for the pushSlideout
// flow) and let only the last-registered instance act on Escape.
const nonModalEscapeStack: string[] = [];

function registerNonModalEscape(id: string): void {
  nonModalEscapeStack.push(id);
}

function unregisterNonModalEscape(id: string): void {
  const index = nonModalEscapeStack.lastIndexOf(id);
  if (index !== -1) {
    nonModalEscapeStack.splice(index, 1);
  }
}

function isTopmostNonModalEscape(id: string): boolean {
  return nonModalEscapeStack[nonModalEscapeStack.length - 1] === id;
}

interface NonModalSlideoutProps {
  /**
   * Panel-level className forwarded from the public `SlideoutMenu.className`
   * prop. Applied to the outer panel element so consumer-supplied
   * positioning/styling keeps working in non-modal mode, matching the modal
   * path (where it lands on `Modal`). Accepts react-aria's string-or-function
   * form; the function is evaluated against the current transition state.
   */
  panelClassName?: AriaModalOverlayProps['className'];
  dialogClassName?: string;
  dialogAriaLabel?: string;
  instance?: string;
  isOpen: boolean | undefined;
  onOpenChange: ((isOpen: boolean) => void) | undefined;
  isKeyboardDismissDisabled: boolean | undefined;
  width: number | undefined;
  isResizing: boolean;
  /**
   * Renders the dialog body (header/content/footer or manual-mode children)
   * for the given transition state and close handler. Injected as a render
   * function rather than a direct import of the modal path's shared
   * inner-body component, so this module doesn't need slideout-menu.tsx to
   * export that private symbol.
   */
  renderBody: (state: AriaModalRenderProps, close: () => void) => ReactNode;
}

export function NonModalSlideout({
  panelClassName,
  dialogClassName,
  dialogAriaLabel,
  instance,
  isOpen,
  onOpenChange,
  isKeyboardDismissDisabled,
  width,
  isResizing,
  renderBody,
}: NonModalSlideoutProps) {
  // Resolve open-state from the controlled props first, then fall back to any
  // `SlideoutMenu.Trigger` (react-aria `DialogTrigger`) above us. Without this
  // fallback the trigger-managed API silently breaks in non-modal mode: the
  // panel has no `isOpen`/`onOpenChange`, so it would render open immediately
  // and have no way to close. Controlled props always win, so the CRM record
  // slideout (which passes both) is unaffected.
  const triggerCtx = useContext(OverlayTriggerStateContext);
  const resolvedIsOpen = isOpen ?? triggerCtx?.isOpen;
  const resolvedOnOpenChange = onOpenChange ?? triggerCtx?.setOpen;

  const zIndex = useStackingZIndex(10);
  // Default closed (not open) when neither a controlled prop nor a trigger
  // provides state — fail closed rather than rendering a panel that can never
  // be dismissed.
  const phase = useSlideoutPhase(resolvedIsOpen ?? false);
  const close = useCallback(
    () => resolvedOnOpenChange?.(false),
    [resolvedOnOpenChange]
  );

  const triggerState = useMemo<AriaModalRenderProps['state']>(
    () =>
      ({
        isOpen: phase === 'open' || phase === 'entering',
        setOpen: (o: boolean) => resolvedOnOpenChange?.(o),
        open: () => resolvedOnOpenChange?.(true),
        close,
        toggle: () =>
          resolvedOnOpenChange?.(!(phase === 'open' || phase === 'entering')),
      }) as AriaModalRenderProps['state'],
    [phase, resolvedOnOpenChange, close]
  );

  const renderState = useMemo<AriaModalRenderProps>(
    () => ({
      isEntering: phase === 'entering',
      isExiting: phase === 'exiting',
      state: triggerState,
      defaultClassName: undefined,
    }),
    [phase, triggerState]
  );

  const modalStyle = useMemo(
    () => computeSlideoutPanelStyle(zIndex, width),
    [zIndex, width]
  );

  // Read via refs so the keydown effect below doesn't need onOpenChange/phase
  // in its dependency array — re-adding a document listener on every phase
  // transition (entering -> open -> exiting) would mean brief windows where
  // Escape is unhandled while the old listener is torn down and the new one
  // attaches; refs let the effect stay mounted for the component's whole
  // lifetime while always reading fresh values.
  const onOpenChangeRef = useRef(resolvedOnOpenChange);
  onOpenChangeRef.current = resolvedOnOpenChange;
  const phaseRef2 = useRef(phase);
  phaseRef2.current = phase;

  // Stable per-instance id used to identify this panel in the module-level
  // Escape stack, so the coordinator can tell which mounted panel is topmost.
  const escapeId = useId();

  // Every *on-screen* panel participates in the stack, whether or not it's
  // keyboard-dismissable. A panel stays registered from the moment it starts
  // entering until it is fully `closed` (unmounted to `null`) — deliberately
  // including its exit animation. Keeping an *exiting* panel in the stack
  // means it remains topmost while it animates out, so Escape can't leak
  // through to the slideout stacked beneath it (still visible behind the
  // animating panel) and close that one instead. The keydown guard below only
  // acts while phase is 'open'/'entering', so the exiting topmost panel simply
  // swallows Escape as a no-op until it reaches 'closed' and drops out, at
  // which point the panel beneath becomes topmost.
  //
  // Crucially, a keyboard-dismiss-disabled panel MUST stay in the stack too:
  // if it's stacked above a dismissable panel and we excluded it, the lower
  // panel would become topmost and Escape would dismiss it out from under the
  // disabled panel on top. Keeping the disabled panel registered lets it own
  // the z-order decision; the keydown effect below still guards so it never
  // dismisses anything itself, so Escape on top of it is simply a no-op.
  const isStackParticipant = phase !== 'closed';

  useEffect(() => {
    if (!isStackParticipant) {
      return;
    }
    registerNonModalEscape(escapeId);
    return () => unregisterNonModalEscape(escapeId);
  }, [isStackParticipant, escapeId]);

  // `data-react-aria-top-layer` (rendered on the panel below) must be present
  // only during the DOM-INSERTION window, then removed. Two react-aria
  // behaviours hang off that one attribute:
  //
  //  1. ariaHideOutside (a react-aria Modal beneath us marking everything
  //     outside itself inert): its walk/MutationObserver consult the
  //     attribute exactly once, when the panel is INSERTED, and record the
  //     panel *element* in a persistent visibleNodes set. The attribute is
  //     therefore only needed at insertion time.
  //  2. useInteractOutside: `target.closest('[data-react-aria-top-layer]')`
  //     disqualifies every pointer event ON EVERY CLICK, for as long as the
  //     attribute exists. Left in place, no popover/menu opened from inside
  //     the slideout can EVER be dismissed by clicking elsewhere within the
  //     slideout (the owner-picker / filter-popover "won't close" bug).
  //
  // Removing the attribute one frame after insertion keeps (1) — the panel
  // element is already registered — while ending (2). The MutationObserver
  // callback runs as a microtask right after the insertion task; rAF runs
  // strictly after that, so the removal can never race ahead of the walk.
  // (react-aria's public `keepVisible()` would be the first-class API for
  // this, but it is not exported from @react-aria/overlays' index.)
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isStackParticipant) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => {
      panelRef.current?.removeAttribute('data-react-aria-top-layer');
    });
    return () => cancelAnimationFrame(raf);
  }, [isStackParticipant]);

  // The keydown listener stays mounted for the component's whole lifetime
  // (deps don't include phase) so there's no window during a phase transition
  // where Escape goes unhandled. Whether it *acts* is decided at event time by
  // the topmost check and the phase ref.
  useEffect(() => {
    if (isKeyboardDismissDisabled) {
      return;
    }
    const h = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        !e.defaultPrevented &&
        // Only the topmost active non-modal panel responds, so stacked
        // slideouts close one at a time instead of all at once.
        isTopmostNonModalEscape(escapeId) &&
        (phaseRef2.current === 'open' || phaseRef2.current === 'entering')
      ) {
        e.stopPropagation();
        onOpenChangeRef.current?.(false);
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isKeyboardDismissDisabled, escapeId]);

  if (typeof document === 'undefined' || phase === 'closed') {
    return null;
  }

  return createPortal(
    <StackingLayer>
      <div
        ref={panelRef}
        // Mark the panel as a react-aria "top layer" element AT INSERTION. A
        // non-modal slideout can be pushed on top of a react-aria Modal (e.g.
        // the record slideout above the fullscreen merge wizard). The modal's
        // ariaHideOutside isolation marks everything outside its own tree
        // `inert` — including this later-portaled panel, which then paints
        // above the modal but is unclickable, so clicks fall through to the
        // modal beneath (first click closed the wizard, not the slideout).
        // `data-react-aria-top-layer` exempts the panel from that walk. The
        // value must be the literal string 'true' on the portal's subtree.
        // NOTE: the attribute is deliberately REMOVED one frame after mount
        // (see the effect above) — leaving it in place permanently disables
        // react-aria outside-interaction dismissal for every popover opened
        // from inside the slideout.
        data-react-aria-top-layer='true'
        data-entering={phase === 'entering' ? '' : undefined}
        data-exiting={phase === 'exiting' ? '' : undefined}
        style={modalStyle}
        className={cx(
          SLIDEOUT_PANEL_BASE,
          !width && 'max-w-100',
          !isResizing && SLIDEOUT_ANIM_ENTER,
          !isResizing && SLIDEOUT_ANIM_EXIT,
          // Forward the public SlideoutMenu `className` so consumer styling
          // survives in non-modal mode, just as the modal path applies it to
          // `Modal`. Evaluate the render-prop form against the current state.
          typeof panelClassName === 'function'
            ? panelClassName({ ...renderState, defaultClassName: undefined })
            : panelClassName
        )}
      >
        <div
          role='dialog'
          aria-label={dialogAriaLabel?.trim() || 'Slideout panel'}
          data-component='slideout'
          data-instance={instance}
          className={cx(SLIDEOUT_DIALOG_INNER, 'relative', dialogClassName)}
        >
          {renderBody(renderState, close)}
        </div>
      </div>
    </StackingLayer>,
    document.body
  );
}
NonModalSlideout.displayName = 'NonModalSlideout';
